/**
 * IXVerify v2 — the dispense wizard (ixv2 WP-3.2; plan §3.2/§4).
 *
 * Full-screen (the one allowed full-screen surface, R7) with the slim wizard
 * header. One continuous engine for both entry modes:
 *   - LIVE CAPTURE: "New Dispense" pre-creates the session and opens the
 *     viewfinder directly (1 tap). Capture is order-free; every asset uploads
 *     and processes while the CRC keeps shooting (R11 — extraction fires the
 *     moment a page's rendition lands, never after label scanning).
 *   - STAGED: opened from the Ready-to-dispense queue with extraction already
 *     green — straight to kit scanning → one attestation tap → published.
 *
 * The server owns the state machine (WP-3.1); this page reflects it and
 * drives the R12 loops (retake / add-page, originals-block-attestation) and
 * the R5 gates (amber fields need an explicit human tap; mismatches never
 * auto-resolve).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../ui';
import * as ixv from '../lib/api';
import {
  attestPlan,
  capturePrompt,
  confirmPayloadFromExtraction,
  derivePages,
  kitProgress,
  stepForSession,
  type CapturePage,
  type ReviewFieldName,
  type WizardStep,
} from '../lib/captureMachine';
import { CaptureUploader, type UploadTask } from '../lib/uploadQueue';
import { makeRendition, type CapturedFrame } from '../lib/image';
import WizardHeader from '../components/wizard/WizardHeader';
import CaptureSurface, { type CaptureMode } from '../components/wizard/CaptureSurface';
import ReviewScreen from '../components/wizard/ReviewScreen';
import AttestScreen from '../components/wizard/AttestScreen';
import { MismatchRecordedScreen, SuccessScreen } from '../components/wizard/ResultScreen';
import SubjectSheet from '../components/wizard/SubjectSheet';
import SecondCheckSheet from '../components/wizard/SecondCheckSheet';
import ReportPreview from '../components/shared/ReportPreview';

const fieldInputCls =
  'w-full px-2.5 py-1.5 rounded-lg border border-gray-300 bg-white text-sm text-gray-900 placeholder-gray-400 focus:border-primary-500 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-150 motion-reduce:transition-none dark:bg-gray-700 dark:border-gray-600 dark:text-gray-100';

export default function Dispense() {
  const { id: routeId } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { currentSite } = useAuth();
  const { toast } = useToast();

  const [sessionId, setSessionId] = useState<string | null>(routeId || null);
  const [detail, setDetail] = useState<ixv.SessionDetail | null>(null);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [view, setView] = useState<'capture' | 'review' | 'attest' | null>(null);
  const [mode, setMode] = useState<CaptureMode>('document');

  // Capture bookkeeping
  const [uploadTasks, setUploadTasks] = useState<UploadTask[]>([]);
  const uploaderRef = useRef<CaptureUploader | null>(null);
  const pageIndexRef = useRef(0);
  const [extracting, setExtracting] = useState(false);
  const extractQueuedRef = useRef(false);
  const [readingLabel, setReadingLabel] = useState(false);
  const [lastKitTick, setLastKitTick] = useState<string | null>(null);
  const [promptDismissedAt, setPromptDismissedAt] = useState<string | null>(null);
  const [replaceKitId, setReplaceKitId] = useState<string | null>(null);
  const kitQueueRef = useRef<Promise<void>>(Promise.resolve());
  // Pilot r1: local page previews (object URLs, fresh captures only) + the
  // per-page soft-discard / capture-override in-flight flags.
  const [pagePreviews, setPagePreviews] = useState<Record<number, string>>({});
  const pagePreviewsRef = useRef<Record<number, string>>({});
  pagePreviewsRef.current = pagePreviews;
  const [discardBusyKey, setDiscardBusyKey] = useState<string | null>(null);
  const [overridingCapture, setOverridingCapture] = useState(false);

  // Review bookkeeping (R5)
  const [touched, setTouched] = useState<Set<string>>(new Set());
  const [edits, setEdits] = useState<Partial<Record<ReviewFieldName, string | null>>>({});
  const [confirming, setConfirming] = useState(false);
  const [subjectMatch, setSubjectMatch] = useState<'matched' | 'unknown' | 'no_study' | null>(null);
  const [teaching, setTeaching] = useState(false);

  // Attest / publish
  const [publishing, setPublishing] = useState(false);
  const [attestError, setAttestError] = useState<string | null>(null);
  const [publishResult, setPublishResult] = useState<ixv.PublishResult | null>(null);

  // Sheets
  const [subjectSheetOpen, setSubjectSheetOpen] = useState(false);
  const [subjectSaving, setSubjectSaving] = useState(false);
  const [subjectError, setSubjectError] = useState<string | null>(null);
  const [manualKitOpen, setManualKitOpen] = useState(false);
  const [manualKitNumber, setManualKitNumber] = useState('');
  const [manualKitReason, setManualKitReason] = useState('');
  const [pasteOpen, setPasteOpen] = useState(false);
  const [pasteText, setPasteText] = useState('');
  const [secondCheckOpen, setSecondCheckOpen] = useState(false);
  const [reportOpen, setReportOpen] = useState(false);
  const [exitOpen, setExitOpen] = useState(false);

  const detailRef = useRef<ixv.SessionDetail | null>(null);
  detailRef.current = detail;

  const refreshDetail = useCallback(async (id: string) => {
    const fresh = await ixv.getSession(id);
    setDetail(fresh);
    return fresh;
  }, []);

  // -- session creation / load ---------------------------------------------
  useEffect(() => {
    let cancelled = false;
    async function boot() {
      try {
        if (routeId) {
          setSessionId(routeId);
          const d = await ixv.getSession(routeId);
          if (cancelled) return;
          setDetail(d);
          // WP-5.1: a resumed session restores its teaching-banner state from
          // the persisted extraction payload.
          if (typeof d.session.extraction?.teaching_mode === 'boolean') {
            setTeaching(d.session.extraction.teaching_mode);
          }
          // Resume paging after the highest stored page (multi-page add-page).
          const maxPage = Math.max(-1, ...d.assets.filter((a) => a.page_index != null).map((a) => a.page_index as number));
          pageIndexRef.current = maxPage + 1;
        } else {
          // §3.2 step 1: session pre-created the moment the wizard opens.
          const session = await ixv.createSession();
          if (cancelled) return;
          setSessionId(session.id);
          navigate(`/ixverify/dispense/${session.id}`, { replace: true });
          setDetail({ session, kits: [], kit_state: { assigned_count: 0, counts: {} as any, all_resolved: false, pass: false }, assets: [], subject: null });
        }
      } catch (err) {
        if (!cancelled) {
          setLoadError(
            ixv.isKillSwitched(err) ? 'IXVerify is currently disabled.' : ixv.errorMessage(err, 'Could not open the dispense session')
          );
        }
      }
    }
    void boot();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [routeId]);

  // -- extraction (fires while capture continues — R11) ---------------------
  const runExtraction = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    if (extracting) {
      extractQueuedRef.current = true;
      return;
    }
    setExtracting(true);
    try {
      const result = await ixv.extractSession(id);
      setSubjectMatch(result.subject_match);
      // WP-5.1: the server derives the §5.4 teaching state from the study's
      // learning profile — the banner shows from the first extraction on.
      if (typeof result.teaching_mode === 'boolean') setTeaching(result.teaching_mode);
      setPromptDismissedAt(null); // fresh verdict → fresh prompt
      await refreshDetail(id);
    } catch (err) {
      if (ixv.isKillSwitched(err)) {
        toast('IXVerify AI is currently disabled — extraction unavailable', { type: 'error' });
      } else if (ixv.errorCode(err) === 'IXV_NO_DOCUMENT') {
        // Nothing to extract yet — quiet.
      } else {
        toast(ixv.errorMessage(err, 'Could not read the assignment'), { type: 'error' });
      }
    } finally {
      setExtracting(false);
      if (extractQueuedRef.current) {
        extractQueuedRef.current = false;
        // A later page landed mid-run — run again so the verdict covers it.
        setTimeout(() => void runExtraction(), 0);
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [sessionId, extracting, refreshDetail, toast]);

  const runExtractionRef = useRef(runExtraction);
  runExtractionRef.current = runExtraction;

  // -- the two-tier uploader -------------------------------------------------
  const uploader = useMemo(() => {
    if (!sessionId) return null;
    const up = new CaptureUploader({
      upload: async (blob, opts) => {
        const res = await ixv.uploadSessionAsset(sessionId, blob, {
          tier: opts.tier,
          kind: opts.kind,
          page_index: opts.page_index,
          link_asset_id: opts.link_asset_id,
          filename: opts.filename,
          source: opts.source,
        });
        return { asset: { id: res.asset.id } };
      },
      onChange: (tasks) => setUploadTasks(tasks),
      // The extraction trigger: the RENDITION landing, never the original (R11).
      onRenditionUploaded: () => void runExtractionRef.current(),
      onOriginalUploaded: () => {
        const id = sessionId;
        if (id) void refreshDetail(id).catch(() => {});
      },
    });
    uploaderRef.current = up;
    return up;
  }, [sessionId, refreshDetail]);

  // -- capture handlers ------------------------------------------------------

  /** Keep a local preview for the thumbnail strip (jsdom-safe; fresh captures only). */
  const rememberPreview = useCallback((pageIndex: number, blob: Blob) => {
    try {
      if (typeof URL === 'undefined' || typeof URL.createObjectURL !== 'function') return;
      const url = URL.createObjectURL(blob);
      setPagePreviews((prev) => ({ ...prev, [pageIndex]: url }));
    } catch {
      /* previews are a nicety — capture never depends on them */
    }
  }, []);

  const releasePreview = useCallback((pageIndex: number) => {
    setPagePreviews((prev) => {
      if (!(pageIndex in prev)) return prev;
      try {
        URL.revokeObjectURL(prev[pageIndex]);
      } catch { /* noop */ }
      const next = { ...prev };
      delete next[pageIndex];
      return next;
    });
  }, []);

  // Revoke every outstanding preview URL on unmount.
  useEffect(() => () => {
    for (const url of Object.values(pagePreviewsRef.current)) {
      try {
        URL.revokeObjectURL(url);
      } catch { /* noop */ }
    }
  }, []);

  const handleDocumentFrame = useCallback(
    async (frame: CapturedFrame) => {
      if (!uploader) return;
      const pageIndex = pageIndexRef.current++;
      let rendition: Blob | null = null;
      try {
        rendition = (await makeRendition(frame.blob)).blob;
      } catch {
        rendition = null; // original-only upload still works (server rasterizes)
      }
      rememberPreview(pageIndex, rendition || frame.blob);
      uploader.captureDocumentPage({ original: frame.blob, rendition, pageIndex, source: 'camera' });
    },
    [uploader, rememberPreview]
  );

  const handleDocumentFile = useCallback(
    async (file: File) => {
      const id = sessionId;
      if (!id) return;
      if (file.type === 'application/pdf') {
        try {
          await ixv.uploadSessionAsset(id, file, { kind: 'ixrs_document', tier: 'original', source: 'upload', filename: file.name });
          await refreshDetail(id);
          void runExtractionRef.current();
        } catch (err) {
          toast(ixv.errorMessage(err, 'Upload failed'), { type: 'error' });
        }
        return;
      }
      // Native-camera / library photo: original uploads UNTOUCHED (R12); the
      // rendition (canvas pass = EXIF normalized) drives extraction.
      if (!uploader) return;
      const pageIndex = pageIndexRef.current++;
      let rendition: Blob | null = null;
      try {
        rendition = (await makeRendition(file)).blob;
      } catch {
        rendition = null;
      }
      rememberPreview(pageIndex, rendition || file);
      uploader.captureDocumentPage({ original: file, rendition, pageIndex, source: 'upload' });
    },
    [sessionId, uploader, refreshDetail, toast, rememberPreview]
  );

  // -- pilot r1/r1.1: per-page discard + the recorded use-anyway override ----
  // The server decides hard-vs-soft from signature coverage and reports it as
  // `mode`; the confirmation phrases what ACTUALLY happened (owner decision
  // 2026-08-21): a pre-signature page is deleted for real, a signed-over page
  // is only removed from this verification (bytes retained).
  const handleDiscardPage = useCallback(
    async (page: CapturePage, intent: 'remove' | 'retake') => {
      const id = sessionId;
      if (!id || !page.discardAssetId || discardBusyKey) return;
      setDiscardBusyKey(page.key);
      try {
        const result = await ixv.discardSessionAsset(id, page.discardAssetId);
        if (page.pageIndex != null) releasePreview(page.pageIndex);
        setPromptDismissedAt(null);
        const fresh = await refreshDetail(id);
        // Surviving pages → re-read so fields + the capture verdict reflect
        // what is actually left (the same trigger a new page firing has).
        const docsLeft = (fresh.assets || []).some(
          (a) => !a.discarded_at && ['ixrs_photo', 'ixrs_document', 'ixrs_text'].includes(a.kind)
        );
        if (docsLeft) void runExtractionRef.current();
        if (intent === 'retake') {
          setMode('document');
          setView('capture');
        }
        const confirmation =
          result.mode === 'deleted'
            ? intent === 'retake'
              ? 'Photo deleted — retake it now'
              : 'Photo deleted'
            : intent === 'retake'
              ? 'Photo removed from this verification — retake it now'
              : 'Photo removed from this verification';
        toast(confirmation, { type: 'success' });
      } catch (err) {
        toast(ixv.errorMessage(err, 'Could not remove the page'), { type: 'error' });
      } finally {
        setDiscardBusyKey(null);
      }
    },
    [sessionId, discardBusyKey, releasePreview, refreshDetail, toast]
  );

  const handleOverrideCapture = useCallback(async () => {
    const id = sessionId;
    if (!id || overridingCapture) return;
    setOverridingCapture(true);
    try {
      await ixv.overrideCaptureCheck(id);
      await refreshDetail(id);
      toast('Photo kept — your decision was recorded', { type: 'success' });
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not record the override'), { type: 'error' });
    } finally {
      setOverridingCapture(false);
    }
  }, [sessionId, overridingCapture, refreshDetail, toast]);

  const tickForKitResult = useCallback((result: ixv.KitScanResult, scannedLabel?: string) => {
    const kit = result.kit || (result.scans && result.scans[result.scans.length - 1]);
    const label = kit?.scanned_kit_number || scannedLabel || '';
    if (result.duplicate) return `${label} — already scanned`;
    if (!kit) return label ? `${label} recorded` : 'Kit recorded';
    switch (kit.match_status) {
      case 'matched':
        return `${label} ✓ matched`;
      case 'manual_confirmed':
        return `${label} ✓ confirmed`;
      case 'pending':
        return `${label} recorded`;
      default:
        return `${label} — needs review`;
    }
  }, []);

  const applyKitResult = useCallback((result: ixv.KitScanResult) => {
    setDetail((prev) =>
      prev
        ? {
            ...prev,
            session: { ...prev.session, ...result.session },
            kits: result.kits,
            kit_state: result.kit_state,
          }
        : prev
    );
  }, []);

  const handleBarcode = useCallback(
    (payload: string) => {
      const id = sessionId;
      if (!id) return;
      // Serialize kit posts — rapid multi-scan must not race the reconciler.
      kitQueueRef.current = kitQueueRef.current.then(async () => {
        try {
          const result = await ixv.addKitFromBarcode(id, payload, replaceKitId || undefined);
          setReplaceKitId(null);
          applyKitResult(result);
          setLastKitTick(tickForKitResult(result));
          if (result.barcode?.expiry && result.barcode.warnings?.some((w) => /expir/i.test(w))) {
            toast('This kit may be expired — check the label date', { type: 'error' });
          }
        } catch (err) {
          if (ixv.errorCode(err) === 'IXV_BARCODE_UNREADABLE') setLastKitTick('Barcode unreadable — try the shutter');
          else if (ixv.isKillSwitched(err)) toast('IXVerify is currently disabled', { type: 'error' });
          else setLastKitTick(ixv.errorMessage(err, 'Scan failed'));
        }
      });
    },
    [sessionId, replaceKitId, applyKitResult, tickForKitResult, toast]
  );

  const handleLabelFrame = useCallback(
    async (frame: CapturedFrame) => {
      const id = sessionId;
      if (!id) return;
      setReadingLabel(true);
      try {
        const result = await ixv.addKitFromPhoto(id, frame.blob, { replaceKitId: replaceKitId || undefined });
        setReplaceKitId(null);
        applyKitResult(result);
        setLastKitTick(tickForKitResult(result));
      } catch (err) {
        const disagreement = ixv.sourcesDisagreement(err);
        if (disagreement) {
          setLastKitTick(`Readings disagree: ${disagreement.barcode.join('/')} vs ${disagreement.vision.join('/')} — rescan`);
        } else if (ixv.errorCode(err) === 'IXV_LABEL_UNREADABLE') {
          setLastKitTick('Label unreadable — retake or type it');
        } else if (ixv.isKillSwitched(err)) {
          toast('IXVerify AI is currently disabled — type the kit number instead', { type: 'error' });
        } else {
          setLastKitTick(ixv.errorMessage(err, 'Label read failed'));
        }
      } finally {
        setReadingLabel(false);
      }
    },
    [sessionId, replaceKitId, applyKitResult, tickForKitResult, toast]
  );

  const handleLabelFile = useCallback(
    (file: File) => {
      void handleLabelFrame({ blob: file, width: 0, height: 0 });
    },
    [handleLabelFrame]
  );

  const submitManualKit = useCallback(async () => {
    const id = sessionId;
    if (!id || !manualKitNumber.trim() || !manualKitReason.trim()) return;
    try {
      const result = await ixv.addKitManual(id, manualKitNumber.trim(), manualKitReason.trim(), replaceKitId || undefined);
      setReplaceKitId(null);
      applyKitResult(result);
      setLastKitTick(tickForKitResult(result));
      setManualKitOpen(false);
      setManualKitNumber('');
      setManualKitReason('');
    } catch (err) {
      toast(ixv.errorMessage(err, 'Manual entry failed'), { type: 'error' });
    }
  }, [sessionId, manualKitNumber, manualKitReason, replaceKitId, applyKitResult, tickForKitResult, toast]);

  const submitPaste = useCallback(async () => {
    const id = sessionId;
    if (!id || !pasteText.trim()) return;
    try {
      await ixv.addSessionText(id, pasteText.trim());
      setPasteOpen(false);
      setPasteText('');
      await refreshDetail(id);
      void runExtractionRef.current();
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not add the pasted text'), { type: 'error' });
    }
  }, [sessionId, pasteText, refreshDetail, toast]);

  // -- review handlers -------------------------------------------------------
  const extraction = detail?.session.extraction ?? null;
  const confirmed = !!detail?.session.assignment?.confirmed_at;

  const handleConfirm = useCallback(async () => {
    const id = sessionId;
    if (!id || !extraction) return;
    setConfirming(true);
    try {
      const payload = confirmPayloadFromExtraction(extraction, edits);
      const result = await ixv.confirmSession(id, { fields: payload.fields, kits: payload.kits });
      setSubjectMatch(result.subject_match);
      // WP-5.1 contract: the confirm response carries teaching_mode; the
      // pre-WP-5.1 study_created heuristic stays as the fallback.
      setTeaching(result.teaching_mode ?? result.study_created);
      setDetail((prev) =>
        prev
          ? { ...prev, session: result.session, kits: result.kits, kit_state: result.kit_state }
          : prev
      );
      // Subject may have re-resolved server-side — pull the full picture.
      await refreshDetail(id);
      if (result.subject_match === 'unknown') setSubjectSheetOpen(true);
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not confirm the assignment'), { type: 'error' });
    } finally {
      setConfirming(false);
    }
  }, [sessionId, extraction, edits, refreshDetail, toast]);

  const handleAddSubject = useCallback(
    async (fields: { subject_number: string; dob: string; initials?: string }) => {
      const id = sessionId;
      if (!id) return;
      setSubjectSaving(true);
      setSubjectError(null);
      try {
        await ixv.attachSessionSubject(id, fields);
        setSubjectSheetOpen(false);
        setSubjectMatch('matched');
        await refreshDetail(id);
      } catch (err) {
        setSubjectError(ixv.errorMessage(err, 'Could not add the subject'));
      } finally {
        setSubjectSaving(false);
      }
    },
    [sessionId, refreshDetail]
  );

  const handleKitDecision = useCallback(
    async (kitId: string, decision: ixv.KitDecision, reason: string) => {
      const id = sessionId;
      if (!id) return;
      try {
        const result = await ixv.decideKit(id, kitId, decision, reason);
        setDetail((prev) => (prev ? { ...prev, kits: result.kits, kit_state: result.kit_state } : prev));
      } catch (err) {
        toast(ixv.errorMessage(err, 'Could not record the decision'), { type: 'error' });
      }
    },
    [sessionId, toast]
  );

  const handleRescanKit = useCallback((kitId: string) => {
    setReplaceKitId(kitId);
    setMode('label');
    setView('capture');
  }, []);

  const handleRecordMismatch = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    try {
      await ixv.recordMismatch(id);
      await refreshDetail(id);
    } catch (err) {
      toast(ixv.errorMessage(err, 'Could not record the mismatch'), { type: 'error' });
    }
  }, [sessionId, refreshDetail, toast]);

  // -- attest + inline publish (R11: one motion) ----------------------------
  const handleAttest = useCallback(
    async (claims: { kits_confirmed: boolean; dob_confirmed: boolean; copy_certified?: boolean }) => {
      const id = sessionId;
      if (!id) return;
      setPublishing(true);
      setAttestError(null);
      try {
        await ixv.attestSession(id, claims);
        const result = await ixv.completeSession(id);
        setPublishResult(result);
        setDetail((prev) => (prev ? { ...prev, session: { ...prev.session, ...result.session } } : prev));
      } catch (err) {
        const code = ixv.errorCode(err);
        if (code === 'IXV_MISMATCH_PRESENT' || code === 'IXV_KITS_UNRESOLVED') {
          setView('review');
          toast(ixv.errorMessage(err, 'Kits are not resolved yet'), { type: 'error' });
        } else if (code === 'IXV_CAPTURE_INCOMPLETE') {
          setView('capture');
          setMode('document');
          setPromptDismissedAt(null);
          toast('The capture check failed — retake or add the missing page', { type: 'error' });
        } else {
          setAttestError(ixv.errorMessage(err, 'Could not attest and publish'));
        }
        try {
          await refreshDetail(id);
        } catch {
          /* keep the current view */
        }
      } finally {
        setPublishing(false);
      }
    },
    [sessionId, refreshDetail, toast]
  );

  // Read the sealed PDF in place (ReportPreview presigns it inline); the
  // download stays available as a button inside the preview.
  const handleViewReport = useCallback(() => {
    if (sessionId) setReportOpen(true);
  }, [sessionId]);

  const handleShare = useCallback(async () => {
    const id = sessionId;
    if (!id) return;
    try {
      const { url } = await ixv.getReportUrl(id, { inline: true });
      if (navigator.share) await navigator.share({ title: 'IXVerify report', url });
      else window.open(url, '_blank', 'noopener');
    } catch {
      /* user cancelled the share sheet */
    }
  }, [sessionId]);

  const handleExit = useCallback(() => {
    const status = detailRef.current?.session.status;
    if (!status || ['completed', 'verified', 'second_check_pending', 'mismatch_recorded', 'cancelled'].includes(status)) {
      navigate('/ixverify');
      return;
    }
    setExitOpen(true);
  }, [navigate]);

  const handleCancelSession = useCallback(async () => {
    const id = sessionId;
    if (!id) {
      navigate('/ixverify');
      return;
    }
    try {
      await ixv.cancelSession(id);
    } catch {
      /* leaving anyway — the session stays resumable */
    }
    navigate('/ixverify');
  }, [sessionId, navigate]);

  // -- derived view ----------------------------------------------------------
  const step: WizardStep = useMemo(() => {
    if (loadError) return 'closed';
    if (!detail) return 'start';
    const server = stepForSession(detail);
    if (server === 'published' || server === 'mismatch' || server === 'closed') return server;
    if (view) return view;
    return server === 'review' ? 'review' : 'capture';
  }, [detail, view, loadError]);

  const prompt = useMemo(() => {
    const p = capturePrompt(extraction);
    if (!p) return null;
    if (promptDismissedAt && extraction?.extracted_at === promptDismissedAt) return null;
    return p;
  }, [extraction, promptDismissedAt]);

  const progress = kitProgress(detail?.kit_state);
  const pendingOriginals = uploader?.pendingOriginalCount() ?? 0;
  const plan = detail ? attestPlan(detail, pendingOriginals) : null;
  const canFinishCapture = !!extraction || (detail?.kits.length ?? 0) > 0 || confirmed;
  const pages = useMemo(() => derivePages(detail?.assets, uploadTasks), [detail?.assets, uploadTasks]);

  // -- render ----------------------------------------------------------------
  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-gray-950" data-testid="dispense-wizard">
      <WizardHeader step={step} siteName={currentSite?.name ?? null} onExit={handleExit} />

      {loadError ? (
        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-6 text-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
        >
          <p className="text-sm text-white/80">{loadError}</p>
          <button
            type="button"
            onClick={() => navigate('/ixverify')}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Back to dashboard
          </button>
        </div>
      ) : step === 'start' ? (
        <div className="flex flex-1 flex-col items-center justify-center gap-3 text-white/70">
          <Loader2 className="h-8 w-8 animate-spin motion-reduce:animate-none" aria-hidden="true" />
          <p className="text-sm">Opening dispense…</p>
        </div>
      ) : step === 'capture' ? (
        <CaptureSurface
          mode={mode}
          onModeChange={setMode}
          uploadTasks={uploadTasks}
          onRetryUpload={(taskId) => uploader?.retry(taskId)}
          pages={pages}
          pagePreviews={pagePreviews}
          onDiscardPage={(page, intent) => void handleDiscardPage(page, intent)}
          discardBusyKey={discardBusyKey}
          kitProgress={progress}
          lastKitTick={lastKitTick}
          prompt={prompt}
          onDismissPrompt={() => setPromptDismissedAt(extraction?.extracted_at ?? 'dismissed')}
          onOverrideCapture={() => void handleOverrideCapture()}
          overridingCapture={overridingCapture}
          extracting={extracting}
          readingLabel={readingLabel}
          onDocumentFrame={(f) => void handleDocumentFrame(f)}
          onDocumentFile={(f) => void handleDocumentFile(f)}
          onPasteText={() => setPasteOpen(true)}
          onBarcodeDetected={handleBarcode}
          onLabelFrame={(f) => void handleLabelFrame(f)}
          onLabelFile={handleLabelFile}
          onManualKit={() => setManualKitOpen(true)}
          onDone={() => setView('review')}
          canFinish={canFinishCapture}
        />
      ) : step === 'review' && detail ? (
        <ReviewScreen
          detail={detail}
          extraction={extraction}
          subject={detail.subject}
          subjectMatch={subjectMatch ?? (detail.subject ? 'matched' : detail.session.site_study_id ? 'unknown' : 'no_study')}
          teaching={teaching}
          touched={touched}
          edits={edits}
          onEditField={(f, v) => {
            setEdits((prev) => ({ ...prev, [f]: v }));
            setTouched((prev) => new Set(prev).add(f));
          }}
          onAcceptField={(f) => setTouched((prev) => new Set(prev).add(f))}
          confirmed={confirmed}
          confirming={confirming}
          onConfirm={() => void handleConfirm()}
          onAddSubject={() => {
            setSubjectError(null);
            setSubjectSheetOpen(true);
          }}
          onKitDecision={(kitId, decision, reason) => void handleKitDecision(kitId, decision, reason)}
          onRescanKit={handleRescanKit}
          onBackToCapture={() => setView('capture')}
          onProceed={() => setView('attest')}
          onRecordMismatch={() => void handleRecordMismatch()}
          canProceed={!!plan?.ready}
        />
      ) : step === 'attest' && detail && plan ? (
        <AttestScreen
          subject={detail.subject}
          plan={plan}
          publishing={publishing}
          error={attestError}
          onBack={() => setView('review')}
          onAttest={(claims) => void handleAttest(claims)}
        />
      ) : step === 'published' && detail ? (
        publishResult ? (
          <SuccessScreen
            result={publishResult}
            onViewReport={handleViewReport}
            onShare={() => void handleShare()}
            canShare={typeof navigator !== 'undefined' && !!navigator.share}
            onRequestSecondCheck={() => setSecondCheckOpen(true)}
            onNewDispense={() => {
              window.location.assign('/ixverify/dispense');
            }}
            onDone={() => navigate('/ixverify')}
          />
        ) : (
          /* Reopened later: the published summary, not the celebration. */
          <div
            className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-6 text-center text-white"
            style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
          >
            <p className="text-lg font-semibold tabular-nums">{detail.session.verification_code}</p>
            <p className="text-sm text-white/80">This dispense is published and sealed.</p>
            <button
              type="button"
              onClick={handleViewReport}
              className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              View report PDF
            </button>
            <button
              type="button"
              onClick={() => navigate('/ixverify')}
              className="inline-flex min-h-[44px] items-center rounded-lg border border-white/30 px-4 py-2 text-sm font-medium text-white/90 hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-primary-500"
            >
              Back to dashboard
            </button>
          </div>
        )
      ) : step === 'mismatch' ? (
        <MismatchRecordedScreen onDone={() => navigate('/ixverify')} />
      ) : (
        <div
          className="flex min-h-0 flex-1 flex-col items-center justify-center gap-3 overflow-y-auto px-6 py-6 text-center text-white/80"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1.5rem)' }}
        >
          <p className="text-sm">This session is closed.</p>
          <button
            type="button"
            onClick={() => navigate('/ixverify')}
            className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-4 py-2 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
          >
            Back to dashboard
          </button>
        </div>
      )}

      {/* Sheets */}
      {subjectSheetOpen && (
        <SubjectSheet
          prefillSubjectNumber={extraction?.fields?.subject_number?.value || ''}
          saving={subjectSaving}
          error={subjectError}
          onClose={() => setSubjectSheetOpen(false)}
          onSubmit={(fields) => void handleAddSubject(fields)}
        />
      )}
      {reportOpen && sessionId && (
        <ReportPreview
          sessionId={sessionId}
          verificationCode={detail?.session.verification_code ?? null}
          onClose={() => setReportOpen(false)}
        />
      )}
      {secondCheckOpen && sessionId && (
        <SecondCheckSheet
          sessionId={sessionId}
          onClose={() => setSecondCheckOpen(false)}
          onRequested={() => {
            setSecondCheckOpen(false);
            toast('Second check requested', { type: 'success' });
          }}
        />
      )}

      {/* Manual kit entry (scan_method='manual' — reason required, renders
          amber on the report's provenance box) */}
      {manualKitOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Type a kit number"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Type a kit number</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              Manual entries are flagged on the report — a reason is required.
            </p>
            <input
              aria-label="Kit number"
              className={`${fieldInputCls} mt-3 font-mono`}
              placeholder="Kit number"
              value={manualKitNumber}
              onChange={(e) => setManualKitNumber(e.target.value)}
            />
            <textarea
              aria-label="Reason for manual entry"
              className={`${fieldInputCls} mt-2`}
              rows={2}
              placeholder="Why manual? (e.g. barcode damaged)"
              value={manualKitReason}
              onChange={(e) => setManualKitReason(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setManualKitOpen(false)}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!manualKitNumber.trim() || !manualKitReason.trim()}
                onClick={() => void submitManualKit()}
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
                data-testid="manual-kit-save"
              >
                Add kit
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Paste assignment text */}
      {pasteOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Paste assignment text"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Paste assignment text</h3>
            <textarea
              aria-label="Assignment text"
              className={`${fieldInputCls} mt-3 font-mono`}
              rows={6}
              placeholder="Paste the IXRS assignment email or text here"
              value={pasteText}
              onChange={(e) => setPasteText(e.target.value)}
            />
            <div className="mt-3 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setPasteOpen(false)}
                className="inline-flex min-h-[44px] items-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700 sm:min-h-0"
              >
                Cancel
              </button>
              <button
                type="button"
                disabled={!pasteText.trim()}
                onClick={() => void submitPaste()}
                className="inline-flex min-h-[44px] items-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:cursor-not-allowed disabled:opacity-50 sm:min-h-0"
              >
                Use this text
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Exit sheet — leaving mid-dispense is safe (sessions resume) */}
      {exitOpen && (
        <div
          className="fixed inset-0 z-50 flex items-end justify-center bg-black/50 p-4 backdrop-blur-sm sm:items-center"
          style={{ paddingBottom: 'calc(env(safe-area-inset-bottom) + 1rem)' }}
          role="dialog"
          aria-modal="true"
          aria-label="Exit dispense"
        >
          <div className="w-full max-w-sm rounded-xl bg-white p-5 shadow-xl dark:bg-gray-800">
            <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Leave this dispense?</h3>
            <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
              You can resume it from the dashboard, or cancel it for good.
            </p>
            <div className="mt-4 flex flex-col gap-2">
              <button
                type="button"
                onClick={() => navigate('/ixverify')}
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-primary-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500"
              >
                Keep for later
              </button>
              <button
                type="button"
                onClick={() => void handleCancelSession()}
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg bg-red-600 px-3 py-1.5 text-sm font-medium text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500"
              >
                Cancel session
              </button>
              <button
                type="button"
                onClick={() => setExitOpen(false)}
                className="inline-flex min-h-[44px] items-center justify-center rounded-lg border border-gray-300 px-3 py-1.5 text-sm font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:border-gray-600 dark:text-gray-200 dark:hover:bg-gray-700"
              >
                Keep dispensing
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
